/**
 * useEstateDerivation — Estate entity derivation engine.
 *
 * Estate has a single CustomerType ('Estate') so there is no multi-way
 * promotion like the Individual dialog does (Individual/Minor/NonCitizen).
 * The derivation engine is still useful because it:
 *
 *   1. Locks `customerType = 'Estate'` (the dialog's contract).
 *   2. Surfaces `requiresSupervisorApproval` when either:
 *       - The probate-court filing is missing (case number + filing state)
 *         AND the Letters variant isn't 'Affidavit' (small-estate escape hatch).
 *       - The letters document (Letters Testamentary OR Letters of
 *         Administration) has not been uploaded yet.
 *     This mirrors the Manager-approval gate from seed 11-entity-rules.sql:230
 *     ("Letters Testamentary verification").
 *   3. Exposes `derivedFrom` to keep the DerivationResult shape parallel to
 *     the Individual hook so downstream code (save-dispatch, audit trails)
 *     can introspect either source without branching.
 *
 * Admin toggles can later gate the supervisor-approval behaviour (e.g. auto-
 * approve for Executor variant when letters are on-file); for now the rules
 * are hardcoded and the hook reads no config.
 *
 * Card reference: docs/port-design/category-cards/estate.md §"Derivation rules"
 */

import { useMemo } from 'react';
import type { RaoPerson } from '../../../types';
import type { AttachedDoc } from '../shared/attachedDocs';
import type { EstateLettersVariant } from './estate-config';

// -----------------------------------------------------------------------------
// Public API — mirrors the shape of Individual's DerivationResult
// -----------------------------------------------------------------------------

export type EstateDerivedCustomerType = 'Estate';

export type EstateDerivationSource = 'baseline' | 'variant' | 'existing-cif';

export interface DerivationResult {
  /** Always 'Estate' for this dialog. */
  readonly customerType: EstateDerivedCustomerType;
  /** Which input drove the derivation (mostly 'baseline' for Estate). */
  readonly derivedFrom: EstateDerivationSource;
  /** Pre-existing stored type before derivation (existing-CIF reload only). */
  readonly promotedFrom?: string;
  /**
   * True when the CIF add/mod cannot commit without a supervisor override.
   * Triggers on: missing probate case (non-Affidavit variants) OR missing
   * Letters upload.
   */
  readonly requiresSupervisorApproval?: boolean;
  /** Human-readable list of approval reasons — rendered in the gate banner. */
  readonly approvalReasons: ReadonlyArray<string>;
}

export interface UseEstateDerivationOptions {
  readonly mode: 'new' | 'existing';
  readonly storedType?: string;
  /** Currently-selected Letters variant (held on person.extra.lettersVariant). */
  readonly lettersVariant?: EstateLettersVariant;
  /** Documents attached to this dialog — used to check Letters presence. */
  readonly attachedDocs?: ReadonlyArray<AttachedDoc>;
}

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

export function useEstateDerivation(
  person: RaoPerson,
  opts: UseEstateDerivationOptions,
): DerivationResult {
  return useMemo<DerivationResult>(() => {
    const reasons: string[] = [];

    // Rule 1: customerType is fixed for this dialog.
    const customerType: EstateDerivedCustomerType = 'Estate';
    const derivedFrom: EstateDerivationSource = 'baseline';

    const variant: EstateLettersVariant | undefined = opts.lettersVariant;

    // Rule 2: missing case filing (Field 255 / 256) for non-Affidavit variants.
    //   - Affidavit variant is the explicit "no formal case" escape hatch
    //     (small-estate) and is allowed to have blank case fields.
    //   - Otherwise, missing case number OR missing state demands supervisor.
    if (variant !== 'Affidavit') {
      const caseNumber =
        typeof person.extra?.idNumber === 'string' ? (person.extra.idNumber as string) : '';
      const caseState =
        typeof person.extra?.idState === 'string' ? (person.extra.idState as string) : '';
      if (!caseNumber.trim() || !caseState.trim()) {
        reasons.push(
          'Probate court case number + filing state are required before CIF commit (non-Affidavit variant).',
        );
      }
    }

    // Rule 3: missing Letters upload for Executor / Administrator variants.
    //   - Affidavit variant requires SMALL_ESTATE_AFFIDAVIT instead.
    //   - Executor → LETTERS_TESTAMENTARY, Administrator → LETTERS_ADMINISTRATION.
    //   - The seed SQL Notes field states these are OR-alternatives; we
    //     check by exact docCode match here.
    const docs = opts.attachedDocs ?? [];
    if (variant === 'Executor') {
      const hasLetters = docs.some((d) => d.docCode === 'LETTERS_TESTAMENTARY');
      if (!hasLetters) {
        reasons.push('Letters Testamentary must be uploaded before CIF commit.');
      }
    } else if (variant === 'Administrator') {
      const hasLetters = docs.some((d) => d.docCode === 'LETTERS_ADMINISTRATION');
      if (!hasLetters) {
        reasons.push('Letters of Administration must be uploaded before CIF commit.');
      }
    } else if (variant === 'Affidavit') {
      const hasAffidavit = docs.some((d) => d.docCode === 'SMALL_ESTATE_AFFIDAVIT');
      if (!hasAffidavit) {
        reasons.push(
          'Small-estate affidavit must be uploaded before CIF commit (no formal probate case).',
        );
      }
    } else {
      // Variant not yet picked — surface as approval block to force the
      // officer to make the Letters choice.
      reasons.push('Select a Letters variant (Executor, Administrator, or Small-estate affidavit).');
    }

    // Existing-CIF promotion shape — kept for parity with Individual hook.
    const promotedFrom =
      opts.mode === 'existing' && opts.storedType && opts.storedType !== customerType
        ? opts.storedType
        : undefined;

    return {
      customerType,
      derivedFrom: promotedFrom ? 'existing-cif' : derivedFrom,
      ...(promotedFrom ? { promotedFrom } : {}),
      ...(reasons.length > 0 ? { requiresSupervisorApproval: true } : {}),
      approvalReasons: reasons,
    };
  }, [
    person.extra?.idNumber,
    person.extra?.idState,
    opts.mode,
    opts.storedType,
    opts.lettersVariant,
    opts.attachedDocs,
  ]);
}
